import { Component, Signal, computed, inject, input, linkedSignal, output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { IonItem, IonLabel, IonSelect, IonSelectOption } from '@ionic/angular/standalone';
import type { SelectChangeEventDetail } from '@ionic/core';
import type { IonSelectCustomEvent } from '@ionic/core/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FORM_INPUT_VALIDATION_MESSAGES } from '@settings/form-validation-settings';
import { MbpFormErrorComponent } from '../mbp-form-error/mbp-form-error.component';

interface SelectOptionView<T extends object> {
  option: T;
  value: unknown;
  label: string;
}

@Component({
  selector: 'mbp-select',
  templateUrl: './mbp-select.component.html',
  styleUrl: './mbp-select.component.scss',
  imports: [IonItem, IonLabel, IonSelect, IonSelectOption, TranslateModule, MbpFormErrorComponent]
})
export class MbpSelectComponent<T extends object> implements ControlValueAccessor {
  labelKey = input.required<string>();
  placeholderKey = input.required<string>();
  layout = input<'row' | 'column'>('row');
  options = input.required<T[]>();
  displayKey = input.required<string>();
  fieldKey = input.required<string>();
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  value = input<unknown>(undefined);
  errorMessages = input<Record<string, string> | undefined>(undefined);

  selectionChange = output<T>();

  isRequired: Signal<boolean> = computed(() => this.required() || (this.control()?.hasValidator(Validators.required) ?? false));

  errorMessage: Signal<string | undefined> = computed(() => this.getError());

  protected internalValue = linkedSignal<unknown>(() => this.value());
  protected viewOptions = computed<SelectOptionView<T>[]>(() => this.options().map((option) => {
    const record = option as Record<string, unknown>;
    return { option, value: record[this.fieldKey()], label: String(record[this.displayKey()] ?? '') };
  }));

  private onChange: (value: unknown) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly translate = inject(TranslateService);
  private readonly ngControl: NgControl | undefined = inject(NgControl, { self: true, optional: true }) ?? undefined;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: unknown): void {
    this.internalValue.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected handleChange(event: IonSelectCustomEvent<SelectChangeEventDetail>): void {
    const nextValue: unknown = event.detail?.value ?? undefined;
    this.internalValue.set(nextValue);
    this.onChange(nextValue);
    const selected = this.viewOptions().find((candidate) => candidate.value === nextValue);
    if (selected) {
      this.selectionChange.emit(selected.option);
    }
  }

  protected markAsTouched(): void {
    this.onTouched();
  }

  private getError(): string | undefined {
    this.internalValue();

    const control = this.control();
    if (!control || !control.invalid || !(control.dirty || control.touched)) {
      return undefined;
    }

    const controlErrors: ValidationErrors | undefined = control.errors ?? undefined;
    if (!controlErrors) {
      return undefined;
    }

    for (const errorName of Object.keys(controlErrors)) {
      const mappedKey = this.errorMessages()?.[errorName] ?? FORM_INPUT_VALIDATION_MESSAGES[errorName];
      if (mappedKey) {
        return this.translate.instant(mappedKey);
      }
    }

    return this.translate.instant('form.validation.errors.invalid');
  }

  private control(): AbstractControl<unknown> | undefined {
    return (this.ngControl?.control as AbstractControl<unknown>) ?? undefined;
  }
}
